import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import {
  Award,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
  Plus,
  Eye,
  Edit,
  Download,
  Search,
  Star,
  X,
  Save,
  Trash2,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  ThumbsUp,
  PlayCircle,
  ChevronDown
} from 'lucide-react';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { useOutcomes, useCreateOutcome, useUpdateOutcome, useApproveOutcome, /* useRejectOutcome, */ useDeleteOutcome } from '../../../hooks/useOutcomes';
import { outcomeApi } from '../../../api/outcomeApi';
import { goalApi } from '../../../api/goalApi';
import { formatDate } from '../../../utils/stringUtils';
import api from '../../../api/apiClient';
import { reviewApi } from '../../../api/reviewApi';
import { getSalaryStructures } from '../../../api/salaryStructureApi';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import ExportModal from '../../../components/common/ExportModal';
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import { formatIndianNumber } from "../../../utils/amountFormatter";
import WarningPopup from '../../../components/WarningPopup';
import useDebounce from '../../../hooks/useDebounce';
import hrService from '../../../api/services/hrService';
import EmployeeSalaryStructureEditor from '../../../components/hrms/EmployeeSalaryStructureEditor';
import {
  buildSalaryStructurePayload,
  mapApiStructureToFormState,
} from '../../../utils/salaryStructureFormUtils';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../../api/config';

const CREATE_EMPLOYEE_DEFAULT_LIMIT = 10;
const CREATE_EMPLOYEE_SEARCH_LIMIT = 50;

const parseMonthlyCtcValue = (value) => {
  const parsed = parseInt(String(value ?? '').trim(), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const getIncrementCtcFromOutcome = (outcome) => {
  const details = outcome?.outcomeDetails;
  if (!details) {
    return { previousMonthlyCtc: null, currentMonthlyCtc: null };
  }
  const previousMonthlyCtc = details.previousMonthlyCtc ?? details.currentSalary;
  const currentMonthlyCtc = details.currentMonthlyCtc ?? details.newSalary;
  if (previousMonthlyCtc == null && currentMonthlyCtc == null) {
    return { previousMonthlyCtc: null, currentMonthlyCtc: null };
  }
  return {
    previousMonthlyCtc: previousMonthlyCtc != null ? Number(previousMonthlyCtc) : null,
    currentMonthlyCtc: currentMonthlyCtc != null ? Number(currentMonthlyCtc) : null,
  };
};

const formatMonthlyCtcAmount = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const amount = Number(value);
  if (amount <= 0) return '—';
  return `₹${new Intl.NumberFormat('en-IN').format(amount)}`;
};

const getCreatedByDisplay = (outcome) => {
  const creator = outcome?.createdBy;
  if (!creator || typeof creator !== 'object') {
    return { name: '', email: '' };
  }
  const name = `${creator.firstName || ''} ${creator.lastName || ''}`.trim();
  return {
    name,
    email: creator.email || '',
  };
};

const SelectedEmployeeInfoCard = ({ name, employeeId, email, role, department, formatRoleLabel }) => {
  if (!name && !employeeId && !email && !role && !department) return null;

  const roleDisplay = role ? formatRoleLabel(role) : '';

  const rows = [
    { label: 'Name', value: name },
    { label: 'Employee ID', value: employeeId },
    { label: 'Email', value: email },
    { label: 'Role', value: roleDisplay },
    { label: 'Department', value: department },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-sm font-medium text-gray-700 mb-3">Employee Details</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map(({ label, value }) => (
          <div key={label}>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-sm font-medium text-gray-900 break-words">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const CreatedByDisplay = ({ outcome }) => {
  const { name, email } = getCreatedByDisplay(outcome);
  return (
    <div>
      <div className="text-sm font-medium text-gray-900 truncate" title={name}>
        {name}
      </div>
      {email ? (
        <div className="text-xs text-gray-500 truncate" title={email}>
          {email}
        </div>
      ) : null}
    </div>
  );
};

const IncrementMonthlyCtcDisplay = ({ outcome, showPercent = true }) => {
  const { previousMonthlyCtc, currentMonthlyCtc } = getIncrementCtcFromOutcome(outcome);
  const pct =
    previousMonthlyCtc > 0 &&
      currentMonthlyCtc != null &&
      !Number.isNaN(currentMonthlyCtc)
      ? ((currentMonthlyCtc - previousMonthlyCtc) / previousMonthlyCtc) * 100
      : null;

  return (
    <div className="space-y-1">
      <div className="text-sm text-gray-900">
        <span className="text-gray-500">Old Monthly CTC:</span>{' '}
        <span className="font-medium">{formatMonthlyCtcAmount(previousMonthlyCtc)}</span>
      </div>
      <div className="text-sm text-gray-900">
        <span className="text-gray-500">New Monthly CTC:</span>{' '}
        <span className="font-medium">{formatMonthlyCtcAmount(currentMonthlyCtc)}</span>
      </div>
      {showPercent && pct != null && !Number.isNaN(pct) && Number(pct) > 0 && (
        <div className="text-xs text-gray-500">
          {Number(pct).toFixed(1).replace(/\.0$/, '')}% increment
        </div>
      )}
    </div>
  );
};

/** Match eligible outcome employees by name, employee ID, or email (partial, case-insensitive). */
const filterEligibleEmployeesBySearch = (eligibleList, searchText) => {
  const query = String(searchText || '').trim().toLowerCase();
  if (!query) {
    return eligibleList;
  }

  return eligibleList.filter((employee) => {
    const name = (employee.label || '').toLowerCase();
    const employeeId = String(employee.employeeId || '').toLowerCase();
    const email = (employee.email || '').toLowerCase();
    return (
      name.includes(query) ||
      employeeId.includes(query) ||
      email.includes(query)
    );
  });
};

/** Resolve email from employee API shapes (dropdown, with-goals, review match). */
const getEmployeeEmailFromRecord = (emp) => {
  if (!emp) return '';
  if (emp.email) return String(emp.email).trim();
  if (emp.officialEmail) return String(emp.officialEmail).trim();
  if (emp.user?.email) return String(emp.user.email).trim();
  if (emp.contactInfo?.personalEmail) return String(emp.contactInfo.personalEmail).trim();
  return '';
};

const mapDropdownEmployeeToOutcomeOption = (emp) => {
  const name = `${emp.personalInfo?.firstName ?? ''} ${emp.personalInfo?.lastName ?? ''}`.trim();
  return {
    label: name,
    value: emp._id,
    employeeId: emp.employeeId ?? '',
    department: emp.employmentInfo?.department ?? '',
    role: emp.employmentInfo?.position ?? '',
    email: getEmployeeEmailFromRecord(emp),
    employeeRecord: emp,
  };
};

// Defined outside parent so typing does not remount and close the dropdown
const SearchSelect = ({
  label,
  value,
  onChange,
  options,
  placeholder = "Search or select",
  required = false,
  error = false,
  helperText,
  showDetails = false,
  name,
  onBlur,
  serverSideSearch = false,
  onQueryChange,
  isOpen: controlledOpen,
  onIsOpenChange,
  searchLoading = false,
  emptyMessage,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const dropdownRef = useRef(null);
  const isControlled = typeof controlledOpen === 'boolean' && typeof onIsOpenChange === 'function';
  const open = isControlled ? controlledOpen : internalOpen;

  const setDropdownOpen = (nextOpen) => {
    if (isControlled) {
      onIsOpenChange(nextOpen);
    } else {
      setInternalOpen(nextOpen);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (open) {
          setDropdownOpen(false);
          if (onBlur && name) {
            onBlur({ target: { name, value } });
          }
        }
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onBlur, name, value, isControlled, onIsOpenChange]);

  const normalized = Array.isArray(options)
    ? options.map((opt) => {
      const mainLabel = opt.label || opt.name || opt.fullName || "";
      const employeeId = opt.employeeId || opt.id || opt._id || opt.value || "";
      const department = opt.department || "";
      const role = opt.role || opt.position || "";
      return {
        label: mainLabel,
        value: opt.value || opt.id || opt._id,
        employeeId,
        department,
        role,
        email: opt.email || getEmployeeEmailFromRecord(opt.raw || opt),
        employeeRecord: opt.employeeRecord || opt.raw?.employeeRecord,
        raw: opt
      };
    })
    : [];
  const filtered = serverSideSearch
    ? normalized
    : normalized.filter((o) => {
      const searchText = query.toLowerCase();
      return o.label.toLowerCase().includes(searchText) ||
        (o.employeeId && String(o.employeeId).toLowerCase().includes(searchText)) ||
        (o.email && o.email.toLowerCase().includes(searchText)) ||
        (o.department && o.department.toLowerCase().includes(searchText)) ||
        (o.role && o.role.toLowerCase().includes(searchText));
    });

  return (
    <div className="relative" ref={dropdownRef}>
      {label ? (
        <label className="block text-sm font-medium text-gray-700">{label}</label>
      ) : null}
      <button
        type="button"
        onClick={() => {
          const nextOpen = !open;
          setDropdownOpen(nextOpen);
          if (!nextOpen) {
            setQuery('');
            onQueryChange?.('');
          }
        }}
        className={`mt-1 w-full border rounded-md p-2 flex items-center justify-between text-left ${error ? 'border-red-500' : 'border-gray-300'}`}
      >
        <span
          className={`flex-1 block truncate ${value ? 'text-gray-900' : 'text-gray-500'}`}
          title={value || placeholder}
        >
          {value || placeholder}
        </span>
        <span className="h-4 w-4 text-gray-500">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2" onMouseDown={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                const nextValue = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setQuery(nextValue);
                onQueryChange?.(nextValue);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setDropdownOpen(false);
                }
              }}
              onKeyPress={(e) => {
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              placeholder={placeholder}
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              autoFocus
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {searchLoading && (
              <div className="px-3 py-3 text-sm text-gray-500 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching employees...</span>
              </div>
            )}
            {!searchLoading && filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                {emptyMessage || 'No results'}
              </div>
            )}
            {!searchLoading && filtered.map((opt, idx) => (
              <button
                key={`${opt.value || opt.label}-${idx}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(opt);
                  setDropdownOpen(false);
                  setQuery('');
                  onQueryChange?.('');
                }}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
              >
                <div className="flex flex-col">
                  <span className="font-medium">{opt.label}</span>
                  {showDetails && (opt.employeeId || opt.role || opt.department) && (
                    <span className="text-xs text-gray-500 mt-0.5">
                      {[opt.employeeId, opt.role, opt.department].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {(helperText || (required && !value && error)) && (
        <p className={`mt-1 text-xs ${error ? 'text-red-600 flex items-center' : 'text-gray-500'}`}>
          {error && <AlertCircle className="h-4 w-4 mr-1" />}
          {helperText || 'Required'}
        </p>
      )}
    </div>
  );
};

const CollapsibleSalaryPanel = ({ title, open, onToggle, variant = 'gray', children }) => {
  const panelClass =
    variant === 'green'
      ? 'border-agrosaf-green/30 bg-white'
      : 'border-gray-200 bg-gray-50/80';

  return (
    <div className={`rounded-xl border overflow-hidden ${panelClass}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-gray-100/80 transition-colors duration-200"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-gray-900">{title}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${open ? 'rotate-180' : ''
            }`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={`border-t border-gray-200 px-4 pb-4 transition-opacity duration-300 ease-in-out ${open ? 'opacity-100' : 'pointer-events-none opacity-0'
              }`}
            aria-hidden={!open}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const getTabPrefix = (tab) => {
  if (tab === 'promotions') return 'prom';
  if (tab === 'incentives') return 'incet';
  return 'inc';
};

const PerformanceOutcomes = ({ initialMode, id: propId, onClose, isDrawer }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = searchParams.get('outcomeTab') || 'increments';
  const [activeTab, setActiveTab] = useState(initialTab);

  const prefix = getTabPrefix(initialTab);
  const [searchTerm, setSearchTerm] = useState(searchParams.get(`${prefix}_search`) || "");
  const [filterType, setFilterType] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState(searchParams.get(`${prefix}_startDate`) || "");
  const [filterEndDate, setFilterEndDate] = useState(searchParams.get(`${prefix}_endDate`) || "");
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterEmployeeId, setFilterEmployeeId] = useState(searchParams.get(`${prefix}_employeeId`) || 'all');
  const [tabCounts, setTabCounts] = useState({ increments: 0, promotions: 0, incentives: 0 });
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createEmployeeSearchQuery, setCreateEmployeeSearchQuery] = useState('');
  const [createEmployeeSearchOpen, setCreateEmployeeSearchOpen] = useState(false);
  const debouncedCreateEmployeeSearch = useDebounce(createEmployeeSearchQuery, 400);
  const [incrementSalaryLoading, setIncrementSalaryLoading] = useState(false);
  const [incrementSalaryEmployeeKey, setIncrementSalaryEmployeeKey] = useState('');
  const [incrementCurrentSalary, setIncrementCurrentSalary] = useState({ monthlyCtc: '', components: [] });
  const [incrementNewSalary, setIncrementNewSalary] = useState({ monthlyCtc: '', components: [] });
  const [incrementSalarySaving, setIncrementSalarySaving] = useState(false);
  const [incrementCurrentSalaryOpen, setIncrementCurrentSalaryOpen] = useState(false);
  const [incrementNewSalaryOpen, setIncrementNewSalaryOpen] = useState(false);
  const [sortField, setSortField] = useState(searchParams.get(`${prefix}_sortBy`) || null);
  const [sortOrder, setSortOrder] = useState(searchParams.get(`${prefix}_sortOrder`) || 'asc');
  const [showCreateValidation, setShowCreateValidation] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [outcomeToDelete, setOutcomeToDelete] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showEditValidation, setShowEditValidation] = useState(false);
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [kpiData, setKpiData] = useState({
    totalOutcomes: 0,
    totalIncrements: 0,
    totalPromotions: 0,
    totalIncentives: 0
  });
  const [page, setPage] = useState(Number(searchParams.get(`${prefix}_page`)) || 1);
  const [limit, setLimit] = useState(Number(searchParams.get(`${prefix}_limit`)) || 10);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [loadingSalaryStructures, setLoadingSalaryStructures] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [applyingId, setApplyingId] = useState(null);

  // Sync active tab & active filters to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const prefix = getTabPrefix(activeTab);

      // Set active tab (use 'outcomeTab' to avoid clashing with parent PerformanceModule's 'tab' param)
      params.set('outcomeTab', activeTab);

      // Clean ALL tab prefixed keys to remove stale filters from other tabs
      const allPrefixes = ['inc', 'prom', 'incet'];
      allPrefixes.forEach(p => {
        params.delete(`${p}_search`);
        params.delete(`${p}_startDate`);
        params.delete(`${p}_endDate`);
        params.delete(`${p}_employeeId`);
        params.delete(`${p}_page`);
        params.delete(`${p}_limit`);
        params.delete(`${p}_sortBy`);
        params.delete(`${p}_sortOrder`);
      });

      if (searchTerm) params.set(`${prefix}_search`, searchTerm);
      if (filterStartDate) params.set(`${prefix}_startDate`, filterStartDate);
      if (filterEndDate) params.set(`${prefix}_endDate`, filterEndDate);
      if (filterEmployeeId && filterEmployeeId !== 'all') params.set(`${prefix}_employeeId`, filterEmployeeId);
      if (page && page !== 1) params.set(`${prefix}_page`, String(page));
      if (limit && limit !== 10) params.set(`${prefix}_limit`, String(limit));
      if (sortField) params.set(`${prefix}_sortBy`, sortField);
      if (sortOrder && sortOrder !== 'asc') params.set(`${prefix}_sortOrder`, sortOrder);

      return params;
    }, { replace: true });
  }, [activeTab, searchTerm, filterStartDate, filterEndDate, filterEmployeeId, page, limit, sortField, sortOrder, setSearchParams]);

  const baseExportFilters = useMemo(() => {
    // Map plural tab keys to singular API outcomeType values
    const outcomeTypeMap = { increments: 'increment', promotions: 'promotion', incentives: 'incentive' };
    const filters = {
      outcomeType: outcomeTypeMap[activeTab] || activeTab,
    };
    if (searchTerm) filters.q = searchTerm;
    if (filterStartDate) filters.createdAtStart = filterStartDate;
    if (filterEndDate) filters.createdAtEnd = filterEndDate;
    if (filterEmployeeId && filterEmployeeId !== 'all') filters.employeeId = filterEmployeeId;
    return filters;
  }, [activeTab, searchTerm, filterStartDate, filterEndDate, filterEmployeeId]);

  const requestBackendExport = React.useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const filters = overrideFilters ?? baseExportFilters;
      const payload = {
        module: "performance-outcomes",
        scope,
        format,
        filters,
        page: overridePage ?? page,
        limit: overrideLimit ?? limit,
        sortBy: "createdAt",
        sortOrder: "desc",
        selectedIds: selectedIds ?? [],
      };

      const response = await authFetch(`${API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Export failed");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
      const fallbackName = `performance-outcomes-export.${format === "xlsx" ? "xlsx" : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page, limit]
  );

  // Clear selection when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [searchTerm, filterType, filterStartDate, filterEndDate, filterEmployee, activeTab, page, limit]);

  // Role options with default permissions (matching AddEmployee.jsx)
  const roleOptions = {
    'super_admin': {
      label: 'Super Admin',
      permissions: [
        'dashboard', 'system_hrms', 'system_crm',
        'recruitment', 'onboarding', 'employee_management', 'hr_backoffice',
        'attendance', 'payroll', 'performance', 'training', 'exit_management',
        'compliance_policy', 'reports',
        'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management',
        'analytics', 'target_achievement'
      ],
      description: 'Full system access with all CRUD operations across all modules'
    },
    'hr_manager': {
      label: 'HR Manager',
      permissions: [
        'dashboard', 'system_hrms', 'recruitment', 'onboarding',
        'employee_management', 'hr_backoffice', 'attendance', 'payroll',
        'performance', 'training', 'exit_management', 'compliance_policy', 'reports'
      ],
      description: 'Complete HRMS department management with full access to all HR functions'
    },
    'hr_executive': {
      label: 'HR Executive',
      permissions: [
        'recruitment', 'onboarding', 'employee_management',
        'attendance', 'training', 'performance'
      ],
      description: 'HR operations and support with operational-level access'
    },
    'employee': {
      label: 'Employee',
      permissions: [
        'self_service', 'my_attendance', 'my_payslip',
        'leave_request', 'my_training', 'my_performance', 'policies', 'submit_resignation'
      ],
      description: 'Employee self-service portal with access to own profile, attendance, payslips, leave, training, and performance'
    },
    'sales_manager': {
      label: 'Sales Manager',
      permissions: [
        'dashboard', 'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management',
        'analytics', 'target_achievement'
      ],
      description: 'Complete CRM and sales team management with full access to all sales functions'
    },
    'sales_executive': {
      label: 'Sales Executive',
      permissions: [
        'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management'
      ],
      description: 'Sales operations and customer engagement with standard operational access'
    },
    'business_relationship_manager': {
      label: 'Business Relationship Manager',
      permissions: [
        'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management'
      ],
      description: 'Business relationship operations and customer engagement with standard operational access'
    },
    'business_development_executive': {
      label: 'Business Development Executive',
      permissions: [
        'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management'
      ],
      description: 'Business development operations and customer engagement with standard operational access'
    },
    'dispatch_manager': {
      label: 'Dispatch Manager',
      permissions: [
        'order_management', 'customer_management', 'analytics'
      ],
      description: 'Logistics and dispatch management with order tracking and customer coordination'
    },
    'support_manager': {
      label: 'Support Manager',
      permissions: [
        'customer_management', 'order_management', 'analytics'
      ],
      description: 'Customer support operations with full access to customer relationships and order tracking'
    },
    'customer': {
      label: 'Customer',
      permissions: [
        'customer_management', 'order_management'
      ],
      description: 'Customer portal access for viewing orders and customer information'
    }
  };

  // Available roles for dropdown
  const availableRoles = [
    { value: 'super_admin', label: 'Super Admin', department: 'IT' },
    { value: 'hr_manager', label: 'HR Manager', department: 'HR' },
    { value: 'hr_executive', label: 'HR Executive', department: 'HR' },
    { value: 'employee', label: 'Employee', department: 'General' },
    { value: 'customer', label: 'Customer', department: 'Support' },
    { value: 'sales_manager', label: 'Sales Manager', department: 'Sales' },
    { value: 'sales_executive', label: 'Sales Executive', department: 'Sales' },
    { value: 'business_relationship_manager', label: 'Business Relationship Manager', department: 'Sales' },
    { value: 'business_development_executive', label: 'Business Development Executive', department: 'Sales' },
    { value: 'dispatch_manager', label: 'Dispatch Manager', department: 'Logistics' },
    { value: 'support_manager', label: 'Support Manager', department: 'Support' }
  ];

  const formatRoleLabel = (roleValue) => {
    if (!roleValue && roleValue !== 0) return roleValue;
    const roleString = String(roleValue).trim();
    if (!roleString) return roleString;

    const normalized = roleString.toLowerCase();
    const roleMatch = availableRoles.find(
      (role) =>
        role.value.toLowerCase() === normalized ||
        role.label.toLowerCase() === normalized
    );

    if (roleMatch) return roleMatch.label;

    return roleString
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
      .join(' ')
      .trim();
  };

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle key down for number inputs (prevent negative signs and decimals)
  const handleNumberKeyDown = (e) => {
    // Prevent negative sign, decimal point, and 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };
  const [createFormData, setCreateFormData] = useState({
    employeeId: '',
    employeeName: '',
    employeeDisplayId: '',
    employeeEmail: '',
    employeeRole: '',
    department: '',
    outcomeType: 'increment',
    outcomeDetails: {},
    effectiveDate: '',
    justification: '',
    managerComments: '',
    hrComments: '',
    finalRating: '',
    incentiveAmount: '',
    status: 'draft'
  });

  // API Integration
  const outcomesQuery = useOutcomes({ autoFetch: false });
  const outcomesLoading = outcomesQuery.loading || outcomesQuery.isLoading;
  const createOutcomeMutation = useCreateOutcome();
  const updateOutcomeMutation = useUpdateOutcome();
  const deleteOutcomeMutation = useDeleteOutcome();
  const approveOutcomeMutation = useApproveOutcome();

  const {
    data: createEmployeeSearchResults,
    isLoading: createEmployeeSearchLoading,
    isFetching: createEmployeeSearchFetching,
  } = useQuery({
    queryKey: ['outcomeCreateEmployeeSearch', debouncedCreateEmployeeSearch],
    queryFn: async () => {
      const trimmed = debouncedCreateEmployeeSearch.trim();
      const params = new URLSearchParams();
      params.append('status', 'active');
      if (trimmed) {
        params.append('limit', String(CREATE_EMPLOYEE_SEARCH_LIMIT));
        params.append('search', trimmed);
      } else {
        params.append('limit', '20');
      }

      const response = await authFetch(
        `${API_BASE_URL}/dropdown-data/employees?${params.toString()}`, {
        credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            },
        }
      );

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      if (data.responseCode === 200) {
        return data.data?.employees || [];
      }
      return [];
    },
    enabled: showCreateModal,
    staleTime: 0,
    retry: 1,
  });

  const createEmployeeSearchPending =
    createEmployeeSearchQuery !== debouncedCreateEmployeeSearch;
  const createEmployeeSearchBusy =
    createEmployeeSearchPending || createEmployeeSearchLoading || createEmployeeSearchFetching;

  const createEmployeeDropdownOptions = useMemo(() => {
    const results = Array.isArray(createEmployeeSearchResults) ? createEmployeeSearchResults : [];
    return results.map(mapDropdownEmployeeToOutcomeOption);
  }, [createEmployeeSearchResults]);

  // Function to fetch KPI data
  const fetchKPI = useCallback(async () => {
    try {
      const response = await outcomeApi.getOutcomeKPI();
      const data = response?.data?.data || response?.data || {};
      setKpiData({
        totalOutcomes: data.totalOutcomes || 0,
        totalIncrements: data.totalIncrements || 0,
        totalPromotions: data.totalPromotions || 0,
        totalIncentives: data.totalIncentives || 0
      });
    } catch (error) {
      // Keep default values on error
    }
  }, []);

  // Load salary structures
  useEffect(() => {
    const loadSalaryStructures = async () => {
      try {
        setLoadingSalaryStructures(true);
        const result = await getSalaryStructures({ status: 'active', limit: 100 });
        let structures = [];
        if (Array.isArray(result?.data?.structures)) {
          structures = result.data.structures;
        } else if (Array.isArray(result?.data)) {
          structures = result.data;
        } else if (Array.isArray(result?.structures)) {
          structures = result.structures;
        } else if (Array.isArray(result)) {
          structures = result;
        }
        const activeStructures = structures.filter(
          (structure) => (structure.status || '').toLowerCase() === 'active'
        );
        setSalaryStructures(activeStructures);
      } catch (error) {
        setSalaryStructures([]);
      } finally {
        setLoadingSalaryStructures(false);
      }
    };
    loadSalaryStructures();
  }, []);

  // Get real data from API
  const outcomesData = outcomesQuery.outcomes || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <AlertCircle className="h-4 w-4" />;
      case 'active': return <TrendingUp className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // --- Validation helpers ---
  const isValidDateValue = (val) => {
    if (!val) return false;
    const dt = new Date(val);
    return !Number.isNaN(dt.getTime());
  };

  const validateOutcomeForm = (data, mode = 'create') => {
    const errors = [];
    const type = (data.outcomeType || '').toString();

    // Common required
    if (!data.employeeId) errors.push('Please select an employee');
    if (type === 'promotion' && !data.department) errors.push('Department is required');
    if (!type || !['increment', 'promotion', 'incentive'].includes(type)) errors.push('Please select a valid outcome type');

    // Type specific
    if (type === 'increment') {
      // Salary structure validation is handled separately in the form
      // No need to validate newSalary here as we're using salary structures
    } else if (type === 'promotion') {
      const oldRole = (data?.outcomeDetails?.oldRole || '').toString().trim();
      const newRole = (data?.outcomeDetails?.newRole || '').toString().trim();
      if (!oldRole || !newRole) {
        errors.push('New role is required for promotion');
      } else if (oldRole.toLowerCase() === newRole.toLowerCase()) {
        errors.push('New role must be different from old role');
      }
    } else if (type === 'incentive') {
      const amt = parseFloat(data.incentiveAmount);
      if (data.incentiveAmount === '' || data.incentiveAmount === undefined || data.incentiveAmount === null) {
        errors.push('Incentive amount is required');
      } else if (isNaN(amt) || amt < 0 || amt > 10000000) {
        errors.push('Incentive amount must be between 0 and 10,000,000');
      }
    }

    return errors;
  };

  const validateField = (name, value, formData) => {
    let error = undefined;
    switch (name) {
      case 'employeeName': // Field name used in SearchSelect
        if (!formData.employeeId) error = "Employee is required";
        break;
      case 'outcomeType':
        if (!value) error = "Outcome type is required";
        break;
      case 'effectiveDate':
        if (formData.outcomeType === 'promotion') {
          if (!value) error = "Effective date is required";
          else {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (selectedDate < today) error = "Effective date cannot be in the past";
          }
        }
        break;
      case 'newSalaryStructureId':
        if (formData.outcomeType === 'increment' && !value) {
          error = "New salary structure is required";
        }
        break;
      case 'newRole':
        if (formData.outcomeType === 'promotion' && !value) {
          error = "New role is required";
        }
        break;
      case 'incentiveAmount':
        if (formData.outcomeType === 'incentive') {
          const amt = parseFloat(value);
          if (value === '' || value === undefined || value === null) {
            error = "Incentive amount is required";
          } else if (isNaN(amt)) {
            error = "Please enter a valid number";
          } else if (amt < 0) {
            error = "Amount cannot be negative";
          } else if (amt > 10000000) {
            error = "Amount cannot exceed ₹1,00,00,000";
          }
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    // For outcomeDetails fields, we need to handle them carefully
    if (name === 'newSalaryStructureId' || name === 'newRole') {
      // These are nested in outcomeDetails usually but handled differently in form
    }
    const error = validateField(name, value, createFormData);
    setCreateErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Clamp and format rating input to [0..5] with at most one decimal digit
  const sanitizeRatingInput = (val) => {
    if (val === undefined || val === null) return '';
    let s = String(val).replace(/[^0-9.]/g, '');
    // allow only one dot
    s = s.replace(/(\..*)\./g, '$1');
    // restrict integer to 1 digit, decimal to 1 digit
    if (s.includes('.')) {
      const [i, d = ''] = s.split('.');
      const i1 = i.slice(0, 1);
      const d1 = d.slice(0, 1);
      s = `${i1}${d1 ? '.' + d1 : ''}`;
    } else {
      s = s.slice(0, 1);
    }
    if (s === '') return '';
    let n = Number(s);
    if (Number.isNaN(n)) return '';
    if (n > 5) n = 5;
    if (n < 0) n = 0;
    // keep at most 1 decimal place
    return String(Math.round(n * 10) / 10);
  };

  const handleCreateOutcome = () => {
    // Default the outcome type based on active tab
    const defaultType =
      activeTab === 'promotions'
        ? 'promotion'
        : activeTab === 'incentives'
          ? 'incentive'
          : 'increment';
    setCreateFormData({
      id: '',
      employeeId: '',
      employeeName: '',
      employeeDisplayId: '',
      employeeEmail: '',
      employeeRole: '',
      department: '',
      outcomeType: defaultType,
      outcomeDetails: {},
      effectiveDate: '',
      justification: '',
      managerComments: '',
      hrComments: '',
      finalRating: '',
      incentiveAmount: '',
      status: 'draft'
    });
    setCreateErrors({});
    setTouched({});
    setShowCreateModal(true);
  };

  const handleViewOutcome = (outcomeId) => {
    // Safety check for outcomesData
    if (!outcomesData || !Array.isArray(outcomesData)) {
      toast.error('Outcomes data not available');
      return;
    }

    // Find the outcome in the outcomes data
    const outcome = outcomesData.find(item => (item._id || item.id) === outcomeId);

    if (outcome) {
      setSelectedOutcome(outcome);
      setShowViewModal(true);
    } else {
      toast.error('Outcome not found');
    }
  };

  const handleEditOutcome = (outcomeId) => {
    // Safety check for outcomesData
    if (!outcomesData || !Array.isArray(outcomesData)) {
      toast.error('Outcomes data not available');
      return;
    }

    // Find the outcome in the outcomes data
    const outcome = outcomesData.find(item => (item._id || item.id) === outcomeId);

    if (outcome) {
      setSelectedOutcome(outcome);
      // Normalize values for edit form
      const outcomeType = outcome.outcomeType || outcome.type || '';
      const isIncrement = outcomeType === 'increment';
      const isPromotion = outcomeType === 'promotion';
      const employeeObj = outcome.employeeId && typeof outcome.employeeId === 'object' ? outcome.employeeId : outcome.employee;
      const employeeId = (outcome.employeeId && outcome.employeeId._id) || outcome.employeeId || employeeObj?._id || '';
      const employeeName = outcome.employeeName || employeeObj?.fullName || outcome.employee || '';
      const department = outcome.department || employeeObj?.employmentInfo?.department || employeeObj?.department || '';
      const effectiveDate = outcome.effectiveDate ? String(outcome.effectiveDate).slice(0, 10) : '';
      const justification = typeof outcome.justification === 'string' ? outcome.justification : (outcome.justification?.overall || '');
      const managerComments = outcome.managerComments || outcome.justification?.details || '';
      const hrComments = outcome.hrComments || outcome.justification?.hrNotes || '';

      if (outcomeType === 'incentive') {
        setCreateFormData({
          id: outcome._id || outcome.id,
          employeeId,
          employeeName,
          employeeDisplayId: employeeObj?.employeeId || '',
          employeeEmail: employeeObj?.email || employeeObj?.personalInfo?.email || '',
          employeeRole: employeeObj?.employmentInfo?.position || employeeObj?.user?.role || '',
          department,
          outcomeType,
          effectiveDate,
          justification,
          managerComments,
          hrComments,
          status: outcome.status || 'draft',
          incentiveAmount: outcome.incentiveAmount ?? outcome.incentive?.amount ?? outcome.outcomeDetails?.incentiveAmount ?? '',
          outcomeDetails: {}
        });
        setCreateErrors({});
        setTouched({});
        setShowCreateModal(true);
        return;
      }

      // Extract salary structure information for increments
      const currentSalaryStructureId = outcome.outcomeDetails?.currentSalaryStructureId
        ? String(outcome.outcomeDetails.currentSalaryStructureId)
        : null;
      const newSalaryStructureId = outcome.outcomeDetails?.newSalaryStructureId
        ? String(outcome.outcomeDetails.newSalaryStructureId)
        : null;
      // Also include salary amounts as fallback if structure IDs are not available
      const { previousMonthlyCtc, currentMonthlyCtc } = getIncrementCtcFromOutcome(outcome);
      const currentSalary = previousMonthlyCtc || (outcome.outcomeDetails?.currentSalary ?? null);
      const newSalary =
        currentMonthlyCtc ||
        (outcome.outcomeDetails?.newSalary ??
          outcome.increment?.newSalary ??
          outcome.increment?.amount ??
          null);

      // Extract position information for promotions
      const oldPosition = outcome.outcomeDetails?.oldPosition ?? outcome.promotion?.fromRole ?? '';
      const newPosition = outcome.outcomeDetails?.newPosition ?? outcome.promotion?.toRole ?? '';
      const newRole = outcome.outcomeDetails?.newRole || '';

      const mapped = {
        employeeId,
        employeeName,
        department,
        outcomeType,
        effectiveDate,
        // Status field removed - outcomes are managed by approval workflow
        justification,
        managerComments,
        hrComments,
        finalRating: outcome.finalRating ?? '',
        outcomeDetails: isIncrement
          ? {
            currentSalaryStructureId,
            newSalaryStructureId,
            currentSalary,
            newSalary
          }
          : isPromotion
            ? {
              oldPosition,
              newPosition,
              newRole
            }
            : outcomeType === 'incentive'
              ? {
                incentiveAmount: outcome.incentive?.amount ?? outcome.outcomeDetails?.incentiveAmount ?? ''
              }
              : (outcome.outcomeDetails || {}),
      };

      setEditFormData(mapped);
      setShowEditModal(true);
    } else {
      toast.error('Outcome not found');
    }
  };

  const handleDeleteOutcome = (outcomeId) => {
    setOutcomeToDelete(outcomeId);
    setIsBulkDelete(false);
    setShowDeleteConfirm(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteConfirm(true);
  };


  const confirmDeleteOutcome = async () => {
    if (!outcomeToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteOutcomeMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} outcome(s) deleted successfully`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} outcome(s) could not be deleted`);
        }
      } else {
        await deleteOutcomeMutation.mutateAsync(outcomeToDelete);
        toast.success('Outcome deleted successfully!');
      }
      outcomesQuery.fetchOutcomes(buildListParams());
      refreshTabCounts();
      fetchKPI();
      setShowDeleteConfirm(false);
      setOutcomeToDelete(null);
      setIsBulkDelete(false);
    } catch (error) {
      if (!isBulkDelete) {
        toast.error('Failed to delete outcome. Please try again.');
      }
    }
  };

  const cancelDeleteOutcome = () => {
    setShowDeleteConfirm(false);
    setOutcomeToDelete(null);
    setIsBulkDelete(false);
  };

  const handleSendNotification = (outcomeId) => {
    toast.success(`Notification sent for outcome ${outcomeId}`);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedOutcome(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedOutcome(null);
    setEditFormData({});
  };

  const closeModals = () => {
    handleCloseCreateModal();
    handleCloseEditModal();
    handleCloseViewModal();
    if (isDrawer && onClose) {
      onClose();
    }
  };
  const handleApplyOutcome = async (outcomeId) => {
    try {
      setApplyingId(outcomeId);
      const response = await api.post(`/performance/outcomes/${outcomeId}/apply`);

      if (response?.data?.success) {
        toast.success(response.data.message || 'Outcome applied successfully');
        outcomesQuery.fetchOutcomes(buildListParams());
        refreshTabCounts();
        fetchKPI();
      } else {
        toast.error(response?.data?.message || 'Failed to apply outcome');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to apply outcome');
    } finally {
      setApplyingId(null);
    }
  };

  const handleApproveOutcome = async (outcomeId) => {
    try {
      setApplyingId(outcomeId);
      await approveOutcomeMutation.mutateAsync(outcomeId, {});
      toast.success('Outcome approved successfully');
      outcomesQuery.fetchOutcomes(buildListParams());
      refreshTabCounts();
      fetchKPI();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve outcome');
    } finally {
      setApplyingId(null);
    }
  };

  const handleSaveEdit = async () => {
    setShowEditValidation(true);

    // Validate required fields
    const errors = [];
    const nextFieldErrors = {};

    if (!editFormData.effectiveDate || editFormData.effectiveDate.trim() === '') {
      errors.push('Effective date is required');
    }

    // Validate new salary structure for increment outcomes
    if ((editFormData.outcomeType || editFormData.type) === 'increment') {
      const hasNewSalaryStructure = editFormData?.outcomeDetails?.newSalaryStructureId !== undefined &&
        editFormData?.outcomeDetails?.newSalaryStructureId !== '' &&
        editFormData?.outcomeDetails?.newSalaryStructureId !== null;
      if (!hasNewSalaryStructure) {
        nextFieldErrors.newSalaryStructureId = 'New salary structure is required';
        errors.push('New salary structure is required');
      }
    }

    // Validate new role for promotion outcomes
    if ((editFormData.outcomeType || editFormData.type) === 'promotion') {
      const hasNewRole = editFormData?.outcomeDetails?.newRole !== undefined &&
        editFormData?.outcomeDetails?.newRole !== '' &&
        editFormData?.outcomeDetails?.newRole !== null;
      const hasNewPosition = editFormData?.outcomeDetails?.newPosition !== undefined &&
        editFormData?.outcomeDetails?.newPosition !== '' &&
        editFormData?.outcomeDetails?.newPosition !== null;

      if (!hasNewRole && !hasNewPosition) {
        nextFieldErrors.newRole = 'New role is required';
        errors.push('New role is required');
      }
    }

    setEditFieldErrors(nextFieldErrors);
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    try {
      const outcomeId = selectedOutcome._id || selectedOutcome.id;
      // Send effectiveDate, justification, and newSalaryStructureId (for increments)
      const payload = {
        effectiveDate: editFormData.effectiveDate,
        justification: editFormData.justification && editFormData.justification.trim()
          ? editFormData.justification.trim()
          : ''
      };

      // Include newSalaryStructureId in outcomeDetails for increment outcomes
      if ((editFormData.outcomeType || editFormData.type) === 'increment' && editFormData.outcomeDetails?.newSalaryStructureId) {
        payload.outcomeDetails = {
          ...(payload.outcomeDetails || {}),
          newSalaryStructureId: editFormData.outcomeDetails.newSalaryStructureId
        };
      }

      // Include incentiveAmount for incentive outcomes
      if ((editFormData.outcomeType || editFormData.type) === 'incentive' && editFormData.outcomeDetails?.incentiveAmount) {
        payload.outcomeDetails = {
          ...(payload.outcomeDetails || {}),
          incentiveAmount: Number(editFormData.outcomeDetails.incentiveAmount)
        };
      }

      // Include newRole and newPosition for promotion outcomes
      if ((editFormData.outcomeType || editFormData.type) === 'promotion' && (editFormData.outcomeDetails?.newRole || editFormData.outcomeDetails?.newPosition)) {
        payload.outcomeDetails = {
          ...(payload.outcomeDetails || {}),
          newRole: editFormData.outcomeDetails.newRole || editFormData.outcomeDetails.newPosition,
          newPosition: editFormData.outcomeDetails.newPosition || editFormData.outcomeDetails.newRole
        };
      }

      await updateOutcomeMutation.mutateAsync(outcomeId, payload);

      toast.success('Outcome updated successfully!');
      closeModals();

      // Refresh the data
      outcomesQuery.fetchOutcomes(buildListParams());
      // Also refresh tab counters to keep badges in sync
      refreshTabCounts();
      // Refresh KPI data
      fetchKPI();
    } catch (error) {
      toast.error('Failed to update outcome. Please try again.');
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetIncrementSalaryState = useCallback(() => {
    setIncrementCurrentSalary({ monthlyCtc: '', components: [] });
    setIncrementNewSalary({ monthlyCtc: '', components: [] });
    setIncrementSalaryLoading(false);
    setIncrementSalaryEmployeeKey('');
    setIncrementCurrentSalaryOpen(false);
    setIncrementNewSalaryOpen(false);
  }, []);

  const loadEmployeeSalaryForIncrement = useCallback(async (employeeId) => {
    if (!employeeId) {
      resetIncrementSalaryState();
      return;
    }
    const key = String(employeeId);
    setIncrementSalaryLoading(true);
    setIncrementSalaryEmployeeKey(key);
    try {
      const result = await hrService.getEmployeeSalaryStructure(employeeId);
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to fetch salary structure');
      }
      const formState = mapApiStructureToFormState(result.data?.currentStructure);
      setIncrementCurrentSalary(formState);
      setIncrementNewSalary({ monthlyCtc: '', components: [] });
    } catch (error) {
      toast.error(error.message || 'Failed to load employee salary structure');
      resetIncrementSalaryState();
    } finally {
      setIncrementSalaryLoading(false);
    }
  }, [resetIncrementSalaryState]);

  const handleIncrementNewSalaryChange = useCallback(({ monthlyCtc, components }) => {
    setIncrementNewSalary({ monthlyCtc, components });
    setCreateErrors((prev) =>
      prev.salaryStructure ? { ...prev, salaryStructure: undefined } : prev
    );
  }, []);

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateEmployeeSearchQuery('');
    setCreateEmployeeSearchOpen(false);
    resetIncrementSalaryState();
    setCreateFormData({
      id: '',
      employeeId: '',
      employeeName: '',
      employeeDisplayId: '',
      employeeEmail: '',
      employeeRole: '',
      department: '',
      outcomeType: 'increment',
      outcomeDetails: {},
      effectiveDate: '',
      justification: '',
      managerComments: '',
      hrComments: '',
      finalRating: '',
      incentiveAmount: '',
      status: 'draft'
    });
  };

  const handleCreateFormChange = (field, value) => {
    setCreateFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const syncCreateFormEmployeeFromOption = useCallback((opt, outcomeTypeValue) => {
    if (!opt?.value) return;

    const email = opt.email || getEmployeeEmailFromRecord(opt.raw || opt);

    setCreateFormData((prev) => ({
      ...prev,
      employeeId: opt.value,
      employeeName: opt.label,
      department: opt.department,
      employeeEmail: email,
      employeeDisplayId: opt.employeeId,
      employeeRole: opt.role,
      outcomeDetails:
        outcomeTypeValue === 'promotion'
          ? { ...prev.outcomeDetails, oldRole: opt.role }
          : prev.outcomeDetails,
    }));
  }, []);

  const handleSaveCreate = async () => {
    setShowCreateValidation(true);


    // Salary increment: update employee compensation directly; log outcome only
    if (createFormData.outcomeType === 'increment') {
      const errors = {};
      if (!createFormData.employeeId) {
        errors.employeeName = 'Employee is required';
      }

      const salaryPayload = buildSalaryStructurePayload(
        incrementNewSalary.monthlyCtc,
        incrementNewSalary.components,
        createFormData.justification
      );
      if (!salaryPayload) {
        errors.salaryStructure = 'Please enter a valid new salary structure (Monthly CTC and components)';
      }

      if (Object.keys(errors).length > 0) {
        setCreateErrors(errors);
        setTouched({ employeeName: true, salaryStructure: true });
        toast.error('Please fix the highlighted errors');
        return;
      }

      const previousMonthlyCtc = parseMonthlyCtcValue(incrementCurrentSalary.monthlyCtc);
      const currentMonthlyCtc = parseMonthlyCtcValue(incrementNewSalary.monthlyCtc);

      try {
        setIncrementSalarySaving(true);

        const updateResult = await hrService.updateEmployee(createFormData.employeeId, {
          salaryStructure: salaryPayload,
        });
        if (!updateResult?.success) {
          throw new Error(updateResult?.error || 'Failed to update employee salary structure');
        }

        const outcomeRecord = {
          cycleId: 'current',
          employeeId: createFormData.employeeId,
          outcomeType: 'increment',
          outcomeDetails: {
            previousMonthlyCtc,
            currentMonthlyCtc,
          },
          justification: createFormData.justification && createFormData.justification.trim() ? createFormData.justification.trim() : '',
        };

        await createOutcomeMutation.mutateAsync(outcomeRecord);

        handleCloseCreateModal();
        outcomesQuery.fetchOutcomes(buildListParams());
        refreshTabCounts();
        fetchKPI();
      } catch (error) {
        if (!error?.message?.includes('Failed to create outcome')) {
          toast.error(error.message || 'Failed to save salary increment');
        }
      } finally {
        setIncrementSalarySaving(false);
      }
      return;
    }

    if (createFormData.outcomeType === 'incentive') {
      const errors = {};
      if (!createFormData.employeeId) {
        errors.employeeName = 'Employee is required';
      }
      const amtError = validateField('incentiveAmount', createFormData.incentiveAmount, createFormData);
      if (amtError) errors.incentiveAmount = amtError;

      if (Object.keys(errors).length > 0) {
        setCreateErrors(errors);
        setTouched({ employeeName: true, incentiveAmount: true });
        toast.error('Please fix the highlighted errors');
        return;
      }

      try {
        const payload = {
          cycleId: 'current',
          employeeId: createFormData.employeeId,
          outcomeType: 'incentive',
          incentiveAmount: parseFloat(createFormData.incentiveAmount),
          justification: createFormData.justification && createFormData.justification.trim() ? createFormData.justification.trim() : '',
        };

        if (createFormData.id) {
          await updateOutcomeMutation.mutateAsync({ id: createFormData.id, ...payload });
        } else {
          await createOutcomeMutation.mutateAsync(payload);
        }

        closeModals();
        outcomesQuery.fetchOutcomes(buildListParams());
        refreshTabCounts();
        fetchKPI();
      } catch (error) {
      }
      return;
    }

    // Validation (promotion)
    const errors = {};

    if (!createFormData.employeeId) {
      errors.employeeName = "Employee is required";
    }

    const dateError = validateField('effectiveDate', createFormData.effectiveDate, createFormData);
    if (dateError) errors.effectiveDate = dateError;

    if (createFormData.outcomeType === 'promotion') {
      if (!createFormData.outcomeDetails?.newRole) {
        errors.newRole = "New role is required";
      }
    }

    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);

      const touchedFields = {
        employeeName: true,
        effectiveDate: true,
        outcomeType: true
      };
      if (createFormData.outcomeType === 'promotion') touchedFields.newRole = true;

      setTouched(touchedFields);
      toast.error("Please fix the highlighted errors");
      return;
    }

    try {
      const outcomeData = {
        cycleId: "current",
        employeeId: createFormData.employeeId,
        outcomeType: createFormData.outcomeType,
        outcomeDetails: createFormData.outcomeDetails,
        effectiveDate: createFormData.effectiveDate,
        justification: createFormData.justification && createFormData.justification.trim() ? createFormData.justification.trim() : '',
        managerComments: createFormData.managerComments,
        hrComments: createFormData.hrComments,
        status: createFormData.status || 'draft'
      };

      if (createFormData.outcomeType === 'promotion' && createFormData.outcomeDetails?.newRole) {
        outcomeData.outcomeDetails = {
          ...outcomeData.outcomeDetails,
          oldPosition: createFormData.outcomeDetails.oldRole,
          newPosition: createFormData.outcomeDetails.newRole
        };
      }

      const finalRatingNum = Number(createFormData.finalRating);
      if (!Number.isNaN(finalRatingNum) && finalRatingNum >= 0 && finalRatingNum <= 5) {
        outcomeData.finalRating = finalRatingNum;
      }

      if (createFormData.id) {
        await updateOutcomeMutation.mutateAsync({ id: createFormData.id, ...outcomeData });
      } else {
        await createOutcomeMutation.mutateAsync(outcomeData);
      }

      // Note: Employee salary/position will be automatically updated by the scheduled job
      // when the effective date arrives. No immediate update needed.
      // Toast message is already shown in the hook, so we don't need to show it again here.

      closeModals();

      // Refresh the data
      outcomesQuery.fetchOutcomes(buildListParams());
      refreshTabCounts();
      // Refresh KPI data
      fetchKPI();
    } catch (error) {
      // Error toast is already handled in the hook
    }
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats(!showInlineSelectedFormats);
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportSelected = async (format) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
    const ids = Array.from(selectedRows);
    if (!ids.length) {
      toast.error('No selected outcomes found to export.');
      return;
    }
    setExportLoading(true);
    const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
    try {
      await requestBackendExport({
        scope: 'selected',
        format,
        selectedIds: ids,
      });
      toast.success(`${ids.length} outcome(s) exported!`, { id: toastId });
      setShowInlineSelectedFormats(false);
    } catch (error) {
      toast.error(error?.message || 'Export failed.', { id: toastId });
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportScoped = React.useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
      try {
        if (scope === 'currentPage' && outcomesData.length === 0) {
          toast.error('No data on current page to export.', { id: toastId });
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.', { id: toastId });
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: { ...baseExportFilters, createdAtStart: startDate, createdAtEnd: endDate },
          });
        } else if (scope === 'all') {
          const outcomeTypeMap = { increments: 'increment', promotions: 'promotion', incentives: 'incentive' };
          await requestBackendExport({
            scope: 'all',
            format,
            overridePage: 1,
            overrideLimit: 1000,
            overrideFilters: {
              outcomeType: outcomeTypeMap[activeTab] || activeTab,
            },
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          toast.dismiss(toastId);
          return;
        }

        toast.success('Export completed successfully', { id: toastId });
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.', { id: toastId });
      } finally {
        setExportLoading(false);
      }
    },
    [outcomesData, baseExportFilters, requestBackendExport]
  );

  const handleSelectAll = (e) => {
    const currentPageIds = filteredOutcomes.map(o => o._id || o.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };


  // Since we now fetch with filters server-side, just use outcomesData directly
  const filteredOutcomes = Array.isArray(outcomesData) ? outcomesData : [];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedOutcomes = useMemo(() => {
    if (!filteredOutcomes) return [];
    if (!sortField) return filteredOutcomes;

    return [...filteredOutcomes].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employee':
          aValue = (a.employeeName || a.employee || '').toLowerCase();
          bValue = (b.employeeName || b.employee || '').toLowerCase();
          break;
        case 'details':
          if (activeTab === 'increments') {
            aValue = getIncrementCtcFromOutcome(a).currentMonthlyCtc ?? 0;
            bValue = getIncrementCtcFromOutcome(b).currentMonthlyCtc ?? 0;
          } else {
            aValue = (a.outcomeDetails?.newPosition ?? '').toLowerCase();
            bValue = (b.outcomeDetails?.newPosition ?? '').toLowerCase();
          }
          break;
        case 'effectiveDate':
          aValue = new Date(a.effectiveDate || 0).getTime();
          bValue = new Date(b.effectiveDate || 0).getTime();
          break;
        case 'createdBy': {
          const aCreator = getCreatedByDisplay(a);
          const bCreator = getCreatedByDisplay(b);
          aValue = `${aCreator.name} ${aCreator.email}`.toLowerCase();
          bValue = `${bCreator.name} ${bCreator.email}`.toLowerCase();
          break;
        }
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredOutcomes, sortField, sortOrder, activeTab]);


  const getTabStats = (tab) => {
    let outcomes = [];

    if (!outcomesData || !Array.isArray(outcomesData)) {
      return { total: 0, pending: 0, approved: 0, rejected: 0, active: 0, completed: 0 };
    }

    // Map tab keys to actual outcome types
    switch (tab) {
      case 'increments':
        outcomes = outcomesData.filter(outcome => outcome.outcomeType === 'increment');
        break;
      case 'promotions':
        outcomes = outcomesData.filter(outcome => outcome.outcomeType === 'promotion');
        break;
      case 'incentives':
        outcomes = outcomesData.filter(outcome => outcome.outcomeType === 'incentive');
        break;
      default:
        outcomes = outcomesData;
    }

    return {
      total: outcomes.length,
      pending: outcomes.filter(o => o.status === 'pending_approval' || o.status === 'pending').length,
      approved: outcomes.filter(o => o.status === 'approved').length,
      rejected: outcomes.filter(o => o.status === 'rejected').length,
      active: outcomes.filter(o => o.status === 'active').length,
      completed: outcomes.filter(o => o.status === 'completed' || o.status === 'implemented').length
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const buildListParams = useCallback(() => {
    const params = {};
    if (activeTab === 'promotions') {
      params.outcomeType = 'promotion';
    } else if (activeTab === 'incentives') {
      params.outcomeType = 'incentive';
    } else {
      params.outcomeType = 'increment';
    }
    if (filterStartDate) params.createdAtStart = filterStartDate;
    if (filterEndDate) params.createdAtEnd = filterEndDate;
    if (filterEmployeeId && filterEmployeeId !== 'all') params.employeeId = filterEmployeeId;
    if (searchTerm) params.q = searchTerm;
    params.page = page;
    params.limit = limit;
    return params;
  }, [activeTab, filterStartDate, filterEndDate, filterEmployeeId, searchTerm, page, limit]);

  // Server-side fetch on filters/search/tab change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      outcomesQuery.fetchOutcomes(buildListParams());
      fetchKPI();
    }, 300);
    return () => clearTimeout(timer);
  }, [buildListParams, fetchKPI]);

  // Helper to refresh tab counts (used by effect and after mutations)
  const refreshTabCounts = useCallback(async () => {
    try {
      const base = {};
      if (filterStartDate) base.createdAtStart = filterStartDate;
      if (filterEndDate) base.createdAtEnd = filterEndDate;
      if (filterEmployeeId && filterEmployeeId !== 'all') base.employeeId = filterEmployeeId;
      if (searchTerm) base.q = searchTerm;

      const [incRes, proRes, insRes] = await Promise.all([
        outcomeApi.getOutcomes({ ...base, outcomeType: 'increment', limit: 1 }),
        outcomeApi.getOutcomes({ ...base, outcomeType: 'promotion', limit: 1 }),
        outcomeApi.getOutcomes({ ...base, outcomeType: 'incentive', limit: 1 })
      ]);

      const incCount = incRes?.data?.pagination?.total || incRes?.pagination?.total || (Array.isArray(incRes?.data) ? incRes.data.length : 0);
      const proCount = proRes?.data?.pagination?.total || proRes?.pagination?.total || (Array.isArray(proRes?.data) ? proRes.data.length : 0);
      const insCount = insRes?.data?.pagination?.total || insRes?.pagination?.total || (Array.isArray(insRes?.data) ? insRes.data.length : 0);

      setTabCounts({ increments: incCount, promotions: proCount, incentives: insCount });
    } catch (e) {
    }
  }, [filterStartDate, filterEndDate, filterEmployeeId, searchTerm]);

  // Keep tab counts in sync with filters and list changes
  useEffect(() => {
    const timer = setTimeout(() => { refreshTabCounts(); }, 250);
    return () => clearTimeout(timer);
  }, [refreshTabCounts, outcomesData]);

  // Handle initial mode from props (for drawer)
  useEffect(() => {
    if (initialMode === 'create') {
      handleCreateOutcome();
    } else if (initialMode === 'view' && propId) {
      handleViewOutcome(propId);
    } else if (initialMode === 'edit' && propId) {
      handleEditOutcome(propId);
    }
  }, [initialMode, propId]);

  const renderViewContent = () => {
    if (!selectedOutcome) return null;
    const o = selectedOutcome;
    const employeeName = o.employeeName || o.employee?.fullName || o.employee || 'N/A';
    const department = o.department || o.employee?.employmentInfo?.department || 'N/A';
    const managerName = o.managerName || o.employee?.employmentInfo?.reportingManager?.fullName;
    const type = o.outcomeType || o.type || 'N/A';
    const effectiveDate = o.effectiveDate ? formatDate(o.effectiveDate, { includeTime: false }) : 'N/A';
    const { previousMonthlyCtc, currentMonthlyCtc } = getIncrementCtcFromOutcome(o);
    const pct =
      previousMonthlyCtc > 0 &&
        currentMonthlyCtc != null &&
        !Number.isNaN(currentMonthlyCtc)
        ? ((currentMonthlyCtc - previousMonthlyCtc) / previousMonthlyCtc) * 100
        : null;
    const oldPosition = o.outcomeDetails?.oldPosition;
    const newPosition = o.outcomeDetails?.newPosition;
    const formattedOldPosition = formatRoleLabel(oldPosition);
    const formattedNewPosition = formatRoleLabel(newPosition);
    const justificationRaw = typeof o.justification === 'string' ? o.justification : (o.justification?.overall || '');
    const justification = justificationRaw && justificationRaw.trim() ? justificationRaw.trim() : null;
    const finalRating = o.finalRating;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-500">Employee</p>
            <p className="font-medium text-gray-900 break-words">{employeeName}</p>
            {managerName ? (
              <p className="text-xs text-gray-500 break-words">Manager: {managerName}</p>
            ) : null}
          </div>
          <div>
            <p className="text-sm text-gray-500">Department</p>
            <p className="font-medium text-gray-900 break-words">{department}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Outcome Type</p>
            <p className="font-medium text-gray-900 capitalize break-words">{type}</p>
          </div>
          {type === 'promotion' && (
            <div>
              <p className="text-sm text-gray-500">Effective Date</p>
              <p className="font-medium text-gray-900 break-words">{effectiveDate}</p>
            </div>
          )}

          {type !== 'promotion' && (() => {
            const { name, email } = getCreatedByDisplay(o);
            return (
              <div>
                <p className="text-sm text-gray-500">Created By</p>
                <p className="font-medium text-gray-900 break-words">{name}</p>
                {email ? (
                  <p className="text-xs text-gray-500 break-words">{email}</p>
                ) : null}
              </div>
            );
          })()}

          {type === 'increment' && (
            <>
              <div>
                <p className="text-sm text-gray-500">Old Monthly CTC</p>
                <p className="font-medium text-gray-900 break-words">
                  {formatMonthlyCtcAmount(previousMonthlyCtc)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">New Monthly CTC</p>
                <p className="font-medium text-gray-900 break-words">
                  {formatMonthlyCtcAmount(currentMonthlyCtc)}
                </p>
              </div>
              {pct != null && !Number.isNaN(pct) && Number(pct) > 0 && (
                <div>
                  <p className="text-sm text-gray-500">Increment</p>
                  <p className="font-medium text-gray-900 break-words">{Number(pct).toFixed(1).replace(/\.0$/, '')}%</p>
                </div>
              )}
            </>
          )}

          {type === 'promotion' && (
            <>
              <div>
                <p className="text-sm text-gray-500">Old Position</p>
                <p className="font-medium text-gray-900 break-words">{formattedOldPosition || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">New Position</p>
                <p className="font-medium text-gray-900 break-words">{formattedNewPosition || 'N/A'}</p>
              </div>
            </>
          )}

          {type === 'incentive' && (
            <div>
              <p className="text-sm text-gray-500">Incentive Amount</p>
              <p className="font-medium text-gray-900 break-words">
                {(() => {
                  const amt = o.incentive?.amount ?? o.outcomeDetails?.incentiveAmount;
                  return amt !== undefined && amt !== null
                    ? formatCurrency(amt)
                    : 'Amount not specified';
                })()}
              </p>
            </div>
          )}
        </div>

        {o.approvedBy && (() => {
          const approver = o.approvedBy;
          const name = typeof approver === 'object'
            ? `${approver.firstName || ''} ${approver.lastName || ''}`.trim()
            : 'Unknown';
          const email = approver.email || '';
          const empId = approver.employeeId || '';
          const dept = approver.department || '';
          const role = approver.role ? formatRoleLabel(approver.role) : '';
          const approvedAtStr = o.approvedAt ? formatDate(o.approvedAt, { includeTime: true }) : '';

          return (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-semibold text-indigo-900 mb-2">Approver Information</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-indigo-50/50 p-4 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="text-sm font-medium text-gray-900">{name}</p>
                </div>
                {empId && (
                  <div>
                    <p className="text-xs text-gray-500">Employee ID</p>
                    <p className="text-sm font-medium text-gray-900">{empId}</p>
                  </div>
                )}
                {email && (
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900 break-all">{email}</p>
                  </div>
                )}
                {dept && (
                  <div>
                    <p className="text-xs text-gray-500">Department</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">{dept.replace(/_/g, ' ')}</p>
                  </div>
                )}
                {role && (
                  <div>
                    <p className="text-xs text-gray-500">Role</p>
                    <p className="text-sm font-medium text-gray-900">{role}</p>
                  </div>
                )}
                {approvedAtStr && (
                  <div>
                    <p className="text-xs text-gray-500">Approved Date & Time</p>
                    <p className="text-sm font-medium text-gray-900">{approvedAtStr}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        <div className="space-y-4">
          {justification && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Justification</p>
              <p className="text-gray-900 break-words whitespace-pre-wrap first-letter:capitalize">{justification}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEditForm = () => {
    if (!selectedOutcome) return null;
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee *
            </label>
            <select
              value={editFormData.employeeId || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed opacity-60"
            >
              <option value="">{editFormData.employeeName}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department
            </label>
            <input
              type="text"
              value={editFormData.department || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Outcome Type *</label>
            <select
              value={editFormData.outcomeType || editFormData.type || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed opacity-60"
            >
              <option value="increment">Salary Increment</option>
              <option value="promotion">Promotion</option>
              <option value="incentive">Incentive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Effective Date *
            </label>
            <input
              type="date"
              min={new Date().toISOString().split('T')[0]}
              value={editFormData.effectiveDate || ''}
              onChange={(e) => handleEditFormChange('effectiveDate', e.target.value)}
              onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          {(editFormData.outcomeType || editFormData.type) === 'incentive' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Incentive Amount (₹) *</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 font-medium">₹</span>
                <input
                  type="number"
                  name="incentiveAmount"
                  min="0"
                  max="10000000"
                  step="0.01"
                  value={editFormData.outcomeDetails?.incentiveAmount || ''}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val.includes('.')) {
                      const [intPart, decPart] = val.split('.');
                      if (decPart.length > 2) {
                        val = `${intPart}.${decPart.slice(0, 2)}`;
                      }
                    }
                    handleEditFormChange('outcomeDetails', { ...editFormData.outcomeDetails, incentiveAmount: val });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault();
                  }}
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          )}

          {(editFormData.outcomeType || editFormData.type) === 'increment' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Salary Structure</label>
                {(() => {
                  const currentStructureId = editFormData.outcomeDetails?.currentSalaryStructureId;
                  const currentStructure = currentStructureId
                    ? salaryStructures.find(s => (s.id || s._id)?.toString() === currentStructureId?.toString())
                    : null;
                  return (
                    <input
                      type="text"
                      readOnly
                      value={currentStructure ? `${currentStructure.name} · ${currentStructure.code || ''}` : 'No structure'}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                    />
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Salary Structure *</label>
                <select
                  value={editFormData.outcomeDetails?.newSalaryStructureId || ''}
                  onChange={(e) => handleEditFormChange('outcomeDetails', { ...editFormData.outcomeDetails, newSalaryStructureId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select structure</option>
                  {salaryStructures.map(s => <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>)}
                </select>
              </div>
            </>
          )}

          {(editFormData.outcomeType || editFormData.type) === 'promotion' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Role</label>
                <input
                  type="text"
                  readOnly
                  value={formatRoleLabel(editFormData.outcomeDetails?.oldRole || editFormData.outcomeDetails?.oldPosition || '')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Role *</label>
                <select
                  value={editFormData.outcomeDetails?.newRole || editFormData.outcomeDetails?.newPosition || ''}
                  onChange={(e) => handleEditFormChange('outcomeDetails', { ...editFormData.outcomeDetails, newRole: e.target.value, newPosition: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  <option value="">Select new role</option>
                  {availableRoles
                    .filter(r => {
                      if (r.value === 'super_admin') return false;
                      const rawOldRole = (editFormData.outcomeDetails?.oldRole || editFormData.outcomeDetails?.oldPosition || '').trim();
                      if (!rawOldRole) return true;
                      // Normalize to snake_case key for comparison
                      // e.g. "Employee" → "employee", "HR Manager" → "hr_manager"
                      const normalizedOldRole = rawOldRole.toLowerCase().replace(/\s+/g, '_');
                      return r.value !== normalizedOldRole && r.label.toLowerCase() !== rawOldRole.toLowerCase();
                    })
                    .map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                </select>
                {(editFormData.outcomeDetails?.newRole || editFormData.outcomeDetails?.newPosition) && roleOptions[editFormData.outcomeDetails.newRole || editFormData.outcomeDetails.newPosition] && (
                  <p className="mt-1 text-xs text-gray-500">
                    {roleOptions[editFormData.outcomeDetails.newRole || editFormData.outcomeDetails.newPosition].description}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Justification</label>
          <textarea
            value={editFormData.justification || ''}
            onChange={(e) => handleEditFormChange('justification', e.target.value)}
            maxLength={1000}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
          <div className="flex justify-end mt-1">
            <span className="text-xs text-gray-500">
              {(editFormData.justification || '').length}/1000 characters
            </span>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={isDrawer ? onClose : handleCloseEditModal}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveEdit} className="bg-agrosaf-green">Save Changes</Button>
        </div>
      </div>
    );
  };

  const renderCreateForm = () => {
    const selectedEmployeeInfo = createFormData.employeeId ? (
      <SelectedEmployeeInfoCard
        name={createFormData.employeeName}
        employeeId={createFormData.employeeDisplayId}
        email={createFormData.employeeEmail}
        role={createFormData.employeeRole}
        department={createFormData.department}
        formatRoleLabel={formatRoleLabel}
      />
    ) : null;

    const renderCreateEmployeeSearch = (onSelected) => (
      <SearchSelect
        label={<span>Employee <span className="text-red-500">*</span></span>}
        value={createFormData.employeeName || ''}
        name="employeeName"
        onBlur={() => {
          setTouched((prev) => ({ ...prev, employeeName: true }));
          const error = !createFormData.employeeId ? 'Employee is required' : undefined;
          setCreateErrors((prev) => ({ ...prev, employeeName: error }));
        }}
        onChange={(opt) => {
          onSelected(opt);
          if (createErrors.employeeName) {
            setCreateErrors((prev) => ({ ...prev, employeeName: undefined }));
          }
        }}
        options={createEmployeeDropdownOptions}
        serverSideSearch
        onQueryChange={setCreateEmployeeSearchQuery}
        isOpen={createEmployeeSearchOpen}
        onIsOpenChange={setCreateEmployeeSearchOpen}
        searchLoading={createEmployeeSearchBusy}
        placeholder={
          createEmployeeSearchLoading
            ? 'Loading employees...'
            : 'Search by name, employee ID, or email'
        }
        emptyMessage={
          debouncedCreateEmployeeSearch.trim()
            ? 'No employees found'
            : 'No employees available'
        }
        showDetails={true}
        required
        error={!!(touched.employeeName && createErrors.employeeName)}
        helperText={touched.employeeName && createErrors.employeeName}
      />
    );

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Outcome Type <span className="text-red-500">*</span></label>
          <div className="flex items-center space-x-6">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="outcomeType"
                value="increment"
                checked={createFormData.outcomeType === 'increment'}
                onChange={(e) => {
                  handleCreateFormChange('outcomeType', e.target.value);
                  setCreateErrors(prev => ({ ...prev, newSalaryStructureId: undefined, newRole: undefined, incentiveAmount: undefined, salaryStructure: undefined }));
                  if (e.target.value !== 'increment') {
                    resetIncrementSalaryState();
                  }
                }}
                className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Salary Increment</span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="outcomeType"
                value="promotion"
                checked={createFormData.outcomeType === 'promotion'}
                onChange={(e) => {
                  handleCreateFormChange('outcomeType', e.target.value);
                  setCreateErrors(prev => ({ ...prev, newSalaryStructureId: undefined, newRole: undefined, incentiveAmount: undefined }));
                }}
                className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Promotion</span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="outcomeType"
                value="incentive"
                checked={createFormData.outcomeType === 'incentive'}
                onChange={(e) => {
                  handleCreateFormChange('outcomeType', e.target.value);
                  setCreateErrors(prev => ({ ...prev, newSalaryStructureId: undefined, newRole: undefined, incentiveAmount: undefined }));
                }}
                className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Incentive</span>
            </label>
          </div>
        </div>

        {createFormData.outcomeType === 'increment' && (
          <div className="space-y-6">
            {renderCreateEmployeeSearch((opt) => {
              syncCreateFormEmployeeFromOption(opt, 'increment');
              loadEmployeeSalaryForIncrement(opt.value);
            })}
            {selectedEmployeeInfo}

            {createFormData.employeeId && (
              <>
                {incrementSalaryLoading ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Loading salary structure...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <CollapsibleSalaryPanel
                      title="Current Salary Structure"
                      open={incrementCurrentSalaryOpen}
                      onToggle={() => setIncrementCurrentSalaryOpen((isOpen) => !isOpen)}
                    >
                      <EmployeeSalaryStructureEditor
                        readOnly
                        hideTitle
                        resetKey={`${incrementSalaryEmployeeKey}-current`}
                        initialMonthlyCtc={incrementCurrentSalary.monthlyCtc}
                        initialComponents={incrementCurrentSalary.components}
                      />
                    </CollapsibleSalaryPanel>
                    <CollapsibleSalaryPanel
                      title="New Salary Structure Form"
                      open={incrementNewSalaryOpen}
                      onToggle={() => setIncrementNewSalaryOpen((isOpen) => !isOpen)}
                      variant="green"
                    >
                      <EmployeeSalaryStructureEditor
                        hideTitle
                        resetKey={`${incrementSalaryEmployeeKey}-new`}
                        initialMonthlyCtc={incrementNewSalary.monthlyCtc}
                        initialComponents={incrementNewSalary.components}
                        onStateChange={handleIncrementNewSalaryChange}
                      />
                    </CollapsibleSalaryPanel>
                  </div>
                )}
                {touched.salaryStructure && createErrors.salaryStructure && (
                  <p className="text-xs text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {createErrors.salaryStructure}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {createFormData.outcomeType === 'incentive' && (
          <div className="space-y-6">
            {renderCreateEmployeeSearch((opt) => {
              syncCreateFormEmployeeFromOption(opt, 'incentive');
            })}
            {selectedEmployeeInfo}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Incentive Amount (₹) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 font-medium">₹</span>
                <input
                  type="number"
                  name="incentiveAmount"
                  min="0"
                  max="10000000"
                  step="0.01"
                  value={createFormData.incentiveAmount}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val.includes('.')) {
                      const [intPart, decPart] = val.split('.');
                      if (decPart.length > 2) {
                        val = `${intPart}.${decPart.slice(0, 2)}`;
                      }
                    }
                    handleCreateFormChange('incentiveAmount', val);
                    const err = validateField('incentiveAmount', val, { ...createFormData, outcomeType: 'incentive' });
                    setCreateErrors((prev) => ({ ...prev, incentiveAmount: err }));
                  }}
                  onBlur={(e) => {
                    setTouched((prev) => ({ ...prev, incentiveAmount: true }));
                    const err = validateField('incentiveAmount', e.target.value, { ...createFormData, outcomeType: 'incentive' });
                    setCreateErrors((prev) => ({ ...prev, incentiveAmount: err }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault();
                  }}
                  placeholder="e.g. 25000.00"
                  className={`w-full pl-7 pr-3 py-2 border rounded-lg ${touched.incentiveAmount && createErrors.incentiveAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
              </div>
              <p className="mt-1 text-xs text-gray-400">Maximum: ₹1,00,00,000 · Up to 2 decimal places</p>
              {touched.incentiveAmount && createErrors.incentiveAmount && (
                <p className="mt-1 text-xs text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {createErrors.incentiveAmount}
                </p>
              )}
            </div>
          </div>
        )}

        {createFormData.outcomeType === 'promotion' && (
          <div className="space-y-6">
            {renderCreateEmployeeSearch((opt) => {
              syncCreateFormEmployeeFromOption(opt, 'promotion');
            })}
            {selectedEmployeeInfo}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Effective Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="effectiveDate"
                  min={new Date().toISOString().split('T')[0]}
                  value={createFormData.effectiveDate}
                  onChange={(e) => {
                    handleCreateFormChange('effectiveDate', e.target.value);
                    const error = validateField('effectiveDate', e.target.value, createFormData);
                    setCreateErrors((prev) => ({ ...prev, effectiveDate: error }));
                  }}
                  onBlur={handleBlur}
                  onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                  className={`w-full px-3 py-2 border rounded-lg ${touched.effectiveDate && createErrors.effectiveDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                {touched.effectiveDate && createErrors.effectiveDate && (
                  <p className="mt-1 text-xs text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {createErrors.effectiveDate}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Role</label>
                <input
                  type="text"
                  readOnly
                  value={formatRoleLabel(createFormData.employeeRole || '')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Role <span className="text-red-500">*</span></label>
                <select
                  name="newRole"
                  value={createFormData.outcomeDetails?.newRole || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleCreateFormChange('outcomeDetails', { ...createFormData.outcomeDetails, newRole: val });
                    if (createErrors.newRole) {
                      setCreateErrors((prev) => ({ ...prev, newRole: undefined }));
                    }
                  }}
                  onBlur={(e) => {
                    setTouched((prev) => ({ ...prev, newRole: true }));
                    if (!e.target.value) {
                      setCreateErrors((prev) => ({ ...prev, newRole: 'New role is required' }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg bg-white ${touched.newRole && createErrors.newRole ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select new role</option>
                  {availableRoles
                    .filter((r) => {
                      if (r.value === 'super_admin') return false;
                      const rawOldRole = (createFormData.employeeRole || '').trim();
                      if (!rawOldRole) return true;
                      const normalizedOldRole = rawOldRole.toLowerCase().replace(/\s+/g, '_');
                      return r.value !== normalizedOldRole && r.label.toLowerCase() !== rawOldRole.toLowerCase();
                    })
                    .map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                </select>
                {touched.newRole && createErrors.newRole && (
                  <p className="mt-1 text-xs text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {createErrors.newRole}
                  </p>
                )}
                {createFormData.outcomeDetails?.newRole && roleOptions[createFormData.outcomeDetails.newRole] && (
                  <p className="mt-1 text-xs text-gray-500">
                    {roleOptions[createFormData.outcomeDetails.newRole].description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Justification</label>
          <textarea
            value={createFormData.justification || ''}
            onChange={(e) => handleCreateFormChange('justification', e.target.value)}
            maxLength={1000}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
          <div className="flex justify-end mt-1">
            <span className="text-xs text-gray-500">
              {(createFormData.justification || '').length}/1000 characters
            </span>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={isDrawer ? onClose : handleCloseCreateModal}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSaveCreate}
            className="bg-agrosaf-green"
            disabled={incrementSalarySaving}
          >
            {incrementSalarySaving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating salary...
              </span>
            ) : (
              createFormData.id ? 'Save Changes' : 'Create Outcome'
            )}
          </Button>
        </div>
      </div>
    );
  };

  if (isDrawer) {
    return (
      <div className="p-6">
        {initialMode === 'create' && renderCreateForm()}
        {initialMode === 'edit' && renderEditForm()}
        {initialMode === 'view' && renderViewContent()}
        {(initialMode === 'view' || initialMode === 'edit') && !selectedOutcome && (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance Outcomes</h1>
          <p className="text-gray-600">Manage increments, promotions and incentives</p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedRows.size > 0 && activeTab !== 'increments' && (
            <PermissionGate permission="performance" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 border-red-600"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="performance" action="read">
            <div className="relative">
              <Button
                variant="outline"
                icon={exportLoading ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ""}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showInlineSelectedFormats ? "rotate-180" : ""}`} />
              </Button>

              <SelectedExportFormatBar
                isOpen={showInlineSelectedFormats}
                onClose={() => setShowInlineSelectedFormats(false)}
                onExport={handleExportSelected}
                loading={exportLoading}
                className="absolute right-0 mt-2 z-50 min-w-[200px]"
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="performance" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleCreateOutcome}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Create Outcome
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Outcome Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Outcomes</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(kpiData.totalOutcomes)}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Award className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Increments</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(kpiData.totalIncrements)}
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Promotions</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(kpiData.totalPromotions)}
              </p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <Award className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Incentives</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(kpiData.totalIncentives)}
              </p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Star className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { key: 'increments', label: 'Increments', count: tabCounts.increments },
              { key: 'promotions', label: 'Promotions', count: tabCounts.promotions },
              { key: 'incentives', label: 'Incentives', count: tabCounts.incentives }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  const targetTab = tab.key;

                  // Reset all filters to defaults when switching tabs
                  setSearchTerm('');
                  setFilterStartDate('');
                  setFilterEndDate('');
                  setFilterEmployeeId('all');
                  setPage(1);
                  setLimit(10);
                  setSortField(null);
                  setSortOrder('asc');

                  // Change active tab
                  setActiveTab(targetTab);
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                  ? 'border-agrosaf-green text-agrosaf-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.label}
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === tab.key
                  ? 'bg-agrosaf-green text-white'
                  : 'bg-gray-100 text-gray-600'
                  }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap items-end gap-4 w-full">
              <div className="relative flex-[2] min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Employee..."
                  value={searchTerm}
                  onChange={(e) => {
                    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                    const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                    setSearchTerm(value);
                    setPage(1);
                  }}
                  onKeyPress={(e) => {
                    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                    if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  maxLength={100}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      const input = document.querySelector('input[placeholder="Search outcomes..."]');
                      if (input) input.focus();
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex-shrink-0">
                <DateRangeFilter
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  onChange={({ startDate, endDate }) => {
                    setFilterStartDate(startDate);
                    setFilterEndDate(endDate);
                    setPage(1);
                  }}
                />
              </div>
              {(searchTerm || filterStartDate || filterEndDate || filterEmployeeId !== 'all') && (
                <div className="relative flex-shrink-0">
                  <Button
                    variant="outline"
                    icon={<X className="h-4 w-4" />}
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStartDate('');
                      setFilterEndDate('');
                      setFilterEmployeeId('all');
                      setPage(1);
                    }}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Outcomes List */}
          {outcomesLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
              <span className="ml-2 text-gray-600">Loading outcomes...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 capitalize">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                        onChange={handleSelectAll}
                        checked={filteredOutcomes.length > 0 && selectedRows.size === filteredOutcomes.length}
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('employee')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Employee</span>
                        {sortField === 'employee' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('details')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{activeTab === 'increments' ? 'Monthly CTC' : activeTab === 'incentives' ? 'Incentive' : 'Role Change'}</span>
                        {sortField === 'details' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>

                    {activeTab === 'promotions' && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('effectiveDate')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Effective Date</span>
                          {sortField === 'effectiveDate' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                    )}
                    {activeTab !== 'promotions' && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('createdBy')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Created By</span>
                          {sortField === 'createdBy' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                    )}
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created At</span>
                        {sortField === 'createdAt' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOutcomes.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <FileText className="h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No outcomes found</h3>
                          {(searchTerm || filterStartDate || filterEndDate || filterEmployeeId !== 'all') && (
                            <p className="text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
                          )}
                          {!searchTerm && !filterStartDate && !filterEndDate && filterEmployeeId === 'all' && (
                            <p className="text-sm text-gray-500">Get started by creating your first outcome.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sortedOutcomes.map((outcome) => (
                      <tr key={outcome._id || outcome.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                            checked={selectedRows.has(outcome._id || outcome.id)}
                            onChange={() => handleSelectRow(outcome._id || outcome.id)}
                          />
                        </td>
                        <td className="px-6 py-4 max-w-xs">
                          <div>
                            <div className="text-sm font-medium text-gray-900 truncate" title={outcome.employeeName || outcome.employee || 'N/A'}>
                              {outcome.employeeName || outcome.employee || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500 truncate" title={outcome.department || 'N/A'}>{outcome.department || 'N/A'}</div>
                            {(() => {
                              const managerDisplay = outcome.managerName || outcome.manager;
                              return managerDisplay && managerDisplay !== 'N/A' ? (
                                <div className="text-xs text-gray-400 truncate" title={`Manager: ${managerDisplay}`}>Manager: {managerDisplay}</div>
                              ) : null;
                            })()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {activeTab === 'increments' && (
                            <IncrementMonthlyCtcDisplay outcome={outcome} />
                          )}
                          {activeTab === 'promotions' && (
                            <div>
                              <div className="text-sm text-gray-900">
                                {(() => {
                                  const rawOldRole = outcome.outcomeDetails?.oldPosition;
                                  const rawNewRole = outcome.outcomeDetails?.newPosition;
                                  const formattedOldRole = formatRoleLabel(rawOldRole);
                                  const formattedNewRole = formatRoleLabel(rawNewRole);

                                  if (formattedOldRole && formattedNewRole) {
                                    return <span>{`${formattedOldRole} → ${formattedNewRole}`}</span>;
                                  }

                                  if (formattedNewRole) {
                                    return <span>{formattedNewRole}</span>;
                                  }

                                  return <span>New position not specified</span>;
                                })()}
                              </div>
                            </div>
                          )}
                          {activeTab === 'incentives' && (
                            <div>
                              <div className="text-sm text-gray-900">
                                {(() => {
                                  const amt = outcome.incentive?.amount ?? outcome.outcomeDetails?.incentiveAmount;
                                  return amt !== undefined && amt !== null
                                    ? formatCurrency(amt)
                                    : 'Amount not specified';
                                })()}
                              </div>
                              <div className="text-xs text-gray-400">One-time payment</div>
                            </div>
                          )}
                          {/* PIP row removed */}
                        </td>
                        {activeTab === 'promotions' && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {outcome.effectiveDate
                              ? formatDate(outcome.effectiveDate, { includeTime: false })
                              : 'N/A'}
                          </td>
                        )}
                        {activeTab !== 'promotions' && (
                          <td className="px-6 py-4 max-w-xs">
                            <CreatedByDisplay outcome={outcome} />
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {outcome.createdAt ?
                            formatDate(outcome.createdAt, { includeTime: true }) :
                            'N/A'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <PermissionGate permission="performance" action="read">
                              <button
                                onClick={() => handleViewOutcome(outcome._id || outcome.id)}
                                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {activeTab !== 'increments' &&
                              !outcome.isApplied &&
                              outcome.status !== 'approved' &&
                              !(outcome.effectiveDate && new Date(outcome.effectiveDate) < new Date().setHours(0, 0, 0, 0)) && (
                                <>
                                  <PermissionGate permission="performance" action="update">
                                    <button
                                      onClick={() => handleEditOutcome(outcome._id || outcome.id)}
                                      className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                                      title="Edit"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                  <PermissionGate permission="performance" action="delete">
                                    <button
                                      onClick={() => handleDeleteOutcome(outcome._id || outcome.id)}
                                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                  <PermissionGate permission="performance" action="update">
                                    {outcome.status !== 'approved' && (
                                      <button
                                        onClick={() => handleApproveOutcome(outcome._id || outcome.id)}
                                        disabled={applyingId === (outcome._id || outcome.id)}
                                        className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                                        title="Approve"
                                      >
                                        {applyingId === (outcome._id || outcome.id) ? (
                                          <div className="h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                          <ThumbsUp className="h-4 w-4" />
                                        )}
                                      </button>
                                    )}
                                  </PermissionGate>
                                </>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {!outcomesLoading && outcomesData.length > 0 && (
            <PaginationControls
              currentPage={outcomesQuery.pagination?.page || 1}
              totalPages={outcomesQuery.pagination?.totalPages || 0}
              totalItems={outcomesQuery.pagination?.total || 0}
              limit={outcomesQuery.pagination?.limit || 10}
              hasNextPage={(outcomesQuery.pagination?.page || 1) < (outcomesQuery.pagination?.totalPages || 0)}
              hasPrevPage={(outcomesQuery.pagination?.page || 1) > 1}
              onPageChange={(newPage) => setPage(newPage)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />
          )}
        </div>
      </div>

      {/* View Outcome Modal */}
      {showViewModal && selectedOutcome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">View Outcome Details</h3>
              <button
                onClick={handleCloseViewModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 capitalize">
              {renderViewContent()}
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={handleCloseViewModal}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Outcome Modal */}
      {showEditModal && selectedOutcome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Outcome</h3>
              <button
                onClick={handleCloseEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {renderEditForm()}
            </div>
          </div>
        </div>
      )}

      {/* Create Outcome Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div
            className={`bg-white rounded-lg shadow-xl w-full mx-4 min-h-[500px] max-h-[90vh] overflow-y-auto ${createFormData.outcomeType === 'increment' ? 'max-w-5xl' : 'max-w-2xl'
              }`}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{createFormData.id ? 'Edit Outcome' : 'Create New Outcome'}</h3>
              <button
                onClick={handleCloseCreateModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {renderCreateForm()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <WarningPopup
        isOpen={showDeleteConfirm}
        onClose={cancelDeleteOutcome}
        onConfirm={confirmDeleteOutcome}
        title={isBulkDelete ? "Delete Outcomes" : "Delete Outcome"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected outcome(s)? This action cannot be undone.`
            : "Are you sure you want to delete this outcome? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Performance Outcomes"
        scopedFormatExport={{
          onExport: handleExportScoped,
          showDateRange: true,
        }}
      />
    </div>
  );
};

export default PerformanceOutcomes;